/**
 * DevizKnowledgeTab - SuperAdmin panel for browsing all activities across all devize
 * Search, filter, and see price statistics from the knowledge base
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search, Database, RefreshCw, Loader2, TrendingUp, BarChart3, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DevizKnowledgeTab() {
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/deviz-knowledge/stats`);
      setStats(res.data);
    } catch (error) {
      console.error("Stats error:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleRebuild = async () => {
    setRebuilding(true);
    try {
      const res = await axios.post(`${API}/deviz-knowledge/rebuild`);
      toast.success(res.data.message);
      fetchStats();
    } catch (error) {
      toast.error("Eroare la actualizarea bazei de cunostinte");
    } finally {
      setRebuilding(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || searchQuery.length < 2) return;
    setSearching(true);
    try {
      const res = await axios.get(`${API}/deviz-knowledge/search?q=${encodeURIComponent(searchQuery)}&limit=30`);
      setSearchResults(res.data.results);
    } catch (error) {
      toast.error("Eroare la cautare");
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="deviz-knowledge-tab">
      {/* Header */}
      <Card className="border-0 shadow-lg bg-gradient-to-r from-violet-50 to-purple-50 border-l-4 border-l-violet-400">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg">
                <Database className="w-5 h-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  Baza de Cunostinte Devize
                  <Badge className="bg-violet-100 text-violet-700 text-[10px] border-0">AI Learn</Badge>
                </CardTitle>
                <p className="text-xs text-slate-500 mt-0.5">Toate activitatile din devizele platformei — preturi medii, cautare, recomandari</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleRebuild} disabled={rebuilding} className="border-violet-300 text-violet-700">
              {rebuilding ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Actualizeaza
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-3 gap-3">
          <Card className="border border-violet-100">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-violet-700">{stats.total_entries}</p>
              <p className="text-xs text-slate-500">Activitati totale</p>
            </CardContent>
          </Card>
          <Card className="border border-violet-100">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-purple-700">{stats.unique_activities}</p>
              <p className="text-xs text-slate-500">Activitati unice</p>
            </CardContent>
          </Card>
          <Card className="border border-violet-100">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-indigo-700">{stats.companies_contributing}</p>
              <p className="text-xs text-slate-500">Firme contribuitoare</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Top Activities */}
      {stats?.top_activities?.length > 0 && (
        <Card className="border border-violet-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-violet-500" /> Top Activitati (cele mai folosite)
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-1">
              {stats.top_activities.map((a, i) => (
                <div key={`top-${i}`} className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-violet-50 text-xs">
                  <div className="flex items-center gap-2 flex-1">
                    <span className="text-violet-400 font-mono w-5">{i + 1}.</span>
                    <span className="font-medium text-slate-700 truncate">{a.name}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <Badge variant="outline" className="text-[10px]">{a.count}x</Badge>
                    <span className="text-violet-600 font-medium">{a.avg_price.toFixed(0)} RON/u</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <Card className="border border-violet-200 shadow-md">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Search className="w-4 h-4 text-violet-500" /> Cauta Activitati
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder="Ex: hidroizolatie, sapa, tencuiala, montaj parchet..."
              className="flex-1 border-violet-200 focus:border-violet-400"
              data-testid="knowledge-search-input"
            />
            <Button onClick={handleSearch} disabled={searching || searchQuery.length < 2} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="knowledge-search-btn">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>

          {searchResults && (
            <div className="space-y-2">
              <p className="text-xs text-slate-500">{searchResults.length} rezultate gasite</p>
              {searchResults.length === 0 ? (
                <p className="text-center text-slate-400 py-4 text-sm">Nicio activitate gasita</p>
              ) : (
                <div className="max-h-[400px] overflow-y-auto space-y-2">
                  {searchResults.map((r, i) => (
                    <div key={`sr-${i}`} className="p-3 rounded-xl border border-violet-100 hover:border-violet-200 bg-white transition-colors">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className="font-medium text-sm text-slate-800">{r.activity_name}</p>
                          <p className="text-[10px] text-slate-400 mt-0.5">UM: {r.um} | Folosita de {r.count}x</p>
                        </div>
                        <Badge className="bg-violet-100 text-violet-700 text-[10px] shrink-0">{r.count}x</Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <div className="bg-blue-50 rounded-lg p-2 text-center">
                          <p className="text-[10px] text-blue-500">Material</p>
                          <p className="text-sm font-bold text-blue-700">{r.avg_material} RON</p>
                          <p className="text-[9px] text-blue-400">{r.min_material} - {r.max_material}</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-2 text-center">
                          <p className="text-[10px] text-green-500">Manopera</p>
                          <p className="text-sm font-bold text-green-700">{r.avg_manopera} RON</p>
                          <p className="text-[9px] text-green-400">{r.min_manopera} - {r.max_manopera}</p>
                        </div>
                      </div>
                      {r.devize?.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-slate-100">
                          <p className="text-[10px] text-slate-400 mb-1">Devize cu aceasta activitate:</p>
                          {r.devize.map((d, j) => (
                            <p key={`dev-${j}`} className="text-[10px] text-slate-500">
                              {d.deviz_number} — {d.beneficiar} ({d.quantity} {r.um}, Mat: {d.material_price}, Man: {d.manopera_price})
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
